import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, TwIconButton, TwDropdown } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaCloudUploadAlt, FaFolderOpen, FaFolderPlus, FaTrashAlt, FaList, FaTh,
  FaSearch, FaArrowLeft, FaDownload, FaTrash, FaChevronLeft, FaChevronRight,
  FaCheck, FaSortAmountDown, FaHome, FaChevronRight as FaChevronRightSep,
  FaTimes,
} from 'react-icons/fa';
import {
  getAssets, setCurrentPage, setPageSize, setCurrentParams,
  getAncestors, resetAncestors, downloadAsset, moveToTrashAsset, getEditorConfig,
  copyAsset, makePublicAsset,
} from 'redux/drive/driveSlice';
import { debounce } from 'helpers/debounce';
import { getSize } from 'helpers/utils';

import DriveGridItem from 'components/app/drive/DriveGridItem';
import DriveListItem from 'components/app/drive/DriveListItem';
import DrivePreviewModal from 'components/app/drive/DrivePreviewModal';
import DriveUploadModal from 'components/app/drive/DriveUploadModal';
import DriveFolderForm from 'components/app/drive/DriveFolderForm';
import DriveDeleteConfirm from 'components/app/drive/DriveDeleteConfirm';

import DriveShareForm from 'components/app/drive/DriveShareForm';
import DriveDeleteForeverConfirm from 'components/app/drive/confirmation/DriveDeleteForeverConfirm';
import DriveDetailDrawer from 'components/app/drive/DriveDetailDrawer';
import DriveGetLinkModal from 'components/app/drive/DriveGetLinkModal';

const DRIVE_ROOT = '/drives/list';

const SORT_OPTIONS = [
  { value: 'name|asc',         label: 'Name A–Z' },
  { value: 'name|desc',        label: 'Name Z–A' },
  { value: 'updated_at|desc',  label: 'Newest' },
  { value: 'updated_at|asc',   label: 'Oldest' },
  { value: 'file_size|desc',   label: 'Largest' },
  { value: 'file_size|asc',    label: 'Smallest' },
];

const PAGE_SIZE_OPTIONS = [
  { value: 50,  label: '50 / page'  },
  { value: 100, label: '100 / page' },
  { value: 200, label: '200 / page' },
  { value: 500, label: '500 / page' },
];

const DriveListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: assetIdParam } = useParams();
  const assetId = assetIdParam || null;

  const {
    data, totalRows, paginate, currentParams, currentFolder, ancestorData, loading,
  } = useSelector((s) => s.drive);
  // Drive create/upload is universal — every user can create folders and upload
  // files into their own space. Visibility is scoped by ownership/sharing on the
  // backend, not by an action permission, so these buttons are always available.

  const [viewMode, setViewMode]   = useState('grid');
  const [sortBy, setSortBy]       = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [search, setSearch]       = useState('');

  const [selectedIds, setSelectedIds] = useState([]);
  const selectedAssets = useMemo(
    () => data.filter((d) => selectedIds.includes(d.id)),
    [data, selectedIds],
  );

  const [previewAsset, setPreviewAsset]   = useState(null);
  const [showUpload, setShowUpload]       = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [renameAsset, setRenameAsset]     = useState(null);
  const [shareAsset, setShareAsset]       = useState(null);
  const [getLinkAsset, setGetLinkAsset]   = useState(null);
  const [detailAsset, setDetailAsset]     = useState(null);
  const [trashTarget, setTrashTarget]     = useState(null);
  const [deleteForeverAsset, setDeleteForeverAsset] = useState(null);

  /* ─── absolute viewport offsets (top below navbar, left after sidebar) ─── */
  const [pageOffsets, setPageOffsets] = useState({ top: 60, left: 0, right: 0, bottom: 0 });
  useEffect(() => {
    const calc = () => {
      const navbarTop      = document.querySelector('.ptl-navbar-top');
      const navbarVertical = document.querySelector('.ptl-navbar-vertical');
      const banner         = document.querySelector('.release-notification-banner');

      const topNavBottom = navbarTop ? navbarTop.getBoundingClientRect().bottom : 0;
      const bannerBottom = banner    ? banner.getBoundingClientRect().bottom : 0;
      const top = Math.max(topNavBottom, bannerBottom, 60);

      const sidebar = navbarVertical?.getBoundingClientRect();
      // Falcon sidebar: position fixed on the left. Width may collapse on mobile.
      const left = sidebar && sidebar.right > 0 && sidebar.width < window.innerWidth ? sidebar.right : 0;

      setPageOffsets({ top, left, right: 0, bottom: 0 });
    };
    calc();
    window.addEventListener('resize', calc);
    // observe sidebar collapse/expand
    const sb = document.querySelector('.ptl-navbar-vertical');
    let mo;
    if (sb && window.MutationObserver) {
      mo = new window.MutationObserver(calc);
      mo.observe(sb, { attributes: true, attributeFilter: ['class', 'style'] });
    }
    return () => {
      window.removeEventListener('resize', calc);
      mo?.disconnect();
    };
  }, []);

  /* ─── data ─── */
  useEffect(() => {
    const next = { ...currentParams, sort: { attribute: sortBy, direction: sortOrder } };
    if (assetId) {
      next.asset_id = assetId;
      dispatch(getAncestors(assetId));
    } else {
      delete next.asset_id;
      dispatch(resetAncestors());
    }
    dispatch(setCurrentParams(next));
    setSelectedIds([]);
  // this effect writes currentParams, so depending on it would loop; run on folder/sort change only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, assetId, sortBy, sortOrder]);

  useEffect(() => {
    if (currentParams?.page) dispatch(getAssets(currentParams));
  }, [currentParams, dispatch]);

  /* ─── handlers ─── */
  const debouncedSearch = useMemo(
    () => debounce((value) => {
      dispatch(setCurrentParams({
        ...currentParams, name_cont: value, page: 1,
        sort: { attribute: sortBy, direction: sortOrder },
      }));
    }, 400),
    [currentParams, sortBy, sortOrder, dispatch],
  );

  const handleSearch = (e) => { setSearch(e.target.value); debouncedSearch(e.target.value); };
  const clearSearch  = () => { setSearch(''); debouncedSearch(''); };

  const handlePageChange = (page) => {
    dispatch(setCurrentPage(page));
    dispatch(setCurrentParams({ ...currentParams, page, per_page: paginate.pageSize, sort: { attribute: sortBy, direction: sortOrder } }));
  };

  const handlePageSizeChange = (newSize) => {
    dispatch(setPageSize(newSize));
    dispatch(setCurrentParams({ ...currentParams, page: 1, per_page: newSize, sort: { attribute: sortBy, direction: sortOrder } }));
  };

  const handleSortChange = (v) => {
    const [attr, dir] = v.split('|');
    setSortBy(attr);
    setSortOrder(dir);
  };

  const toggleSelect = (item) => {
    setSelectedIds((prev) =>
      prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id],
    );
  };

  const isAllSelected = data.length > 0 && data.every((d) => selectedIds.includes(d.id));
  const toggleSelectAll = () => setSelectedIds(isAllSelected ? [] : data.map((d) => d.id));

  const handleDownload = useCallback((item) => {
    const fileName = item.asset_type === 'folder' ? `${item.name}.zip` : item.name;
    dispatch(downloadAsset({ assetId: item.id, fileName }));
  }, [dispatch]);

  const handleOpen = useCallback((item) => {
    if (item.asset_type === 'folder') {
      navigate(`/drives/${item.id}`);
      return;
    }
    setPreviewAsset(item);
  }, [navigate]);

  const handleAction = useCallback((item, action) => {
    switch (action) {
      case 'preview':       setPreviewAsset(item); break;
      case 'download':      handleDownload(item); break;
      case 'getLink':       setGetLinkAsset(item); break;
      case 'share':         setShareAsset(item); break;
      case 'detail':        setDetailAsset(item); break;
      case 'rename':        setRenameAsset(item); break;
      case 'copy':          dispatch(copyAsset(item.id)); break;
      case 'makePublic':    dispatch(makePublicAsset(item.id)); break;
      case 'moveToTrash':   setTrashTarget(item); break;
      case 'deleteForever': setDeleteForeverAsset(item); break;
      case 'view-online':
      case 'edit-online': {
        const forceView = action === 'view-online';
        dispatch(getEditorConfig({ assetId: item.id, forceView })).unwrap()
          .then((config) => navigate(`/drives/${item.id}/editor`, {
            state: { config, asset: item, configEndpoint: `/drive/assets/${item.id}/editor_config` },
          }))
          .catch(() => toast.error('Failed to load editor configuration'));
        break;
      }
      default: break;
    }
  }, [dispatch, handleDownload, navigate]);

  const handleBulkDownload = () => {
    selectedAssets.forEach(handleDownload);
    toast.info(`Downloading ${selectedAssets.length} item(s)...`);
  };

  const performBulkTrash = async () => {
    try {
      for (const item of selectedAssets) {
        if (item.permissions?.allow_move_to_trash) {
          await dispatch(moveToTrashAsset(item.id)).unwrap();
        }
      }
      toast.success(`Moved ${selectedAssets.length} item(s) to trash`);
      setSelectedIds([]);
      dispatch(getAssets(currentParams));
    } catch { toast.error('Failed to move some items to trash'); }
  };

  const refetch = () => dispatch(getAssets(currentParams));

  /* ─── derived ─── */
  const folderCount = data.filter((d) => d.asset_type === 'folder').length;
  const fileCount   = data.length - folderCount;
  const totalBytes  = data.reduce((sum, d) => sum + (d.asset_type === 'file' ? (d.file_size || 0) : 0), 0);
  const totalPages  = paginate.totalPagesCount || 0;
  const currentPage = paginate.currentPage || 1;
  const pageSize    = paginate.pageSize || 100;
  const fromRecord  = paginate.fromRecord || 0;
  const toRecord    = paginate.toRecord || 0;
  const sortValue   = `${sortBy}|${sortOrder}`;

  // Parent for "up" navigation (breadcrumb back button)
  const parentUrl = (() => {
    if (!assetId) return null; // already at root
    if (ancestorData?.length) {
      const parent = ancestorData[ancestorData.length - 1];
      return `/drives/${parent.id}`;
    }
    return DRIVE_ROOT;
  })();

  return (
    <div
      className="tw-flex tw-min-h-0 tw-flex-col tw-overflow-hidden tw-bg-white tw-m-4 tw-rounded-lg tw-shadow-xl"
      style={{
        position: 'fixed',
        top: pageOffsets.top,
        left: pageOffsets.left,
        right: pageOffsets.right,
        bottom: pageOffsets.bottom,
        zIndex: 1,
      }}
    >
      {/* ── HEADER ─────────────────────────────────────── */}
      <div className="tw-flex-shrink-0 tw-bg-white tw-border-b tw-border-slate-200 tw-z-30">
        {/* Title row */}
        <div className="tw-px-4 sm:tw-px-6 tw-pt-4 tw-pb-3">
          <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between tw-gap-3">
            <div className="tw-flex tw-items-baseline tw-gap-3">
              <h1
                className="tw-text-2xl tw-font-black tw-tracking-tighter tw-m-0"
                style={{
                  backgroundImage: 'linear-gradient(to right, #0f172a, #3b82f6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                {currentFolder || 'My Drive'}
              </h1>
            </div>

            <div className="tw-flex tw-items-center tw-gap-2">
              {/* Search pill — fully overridden inline, no Bootstrap inheritance */}
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  paddingLeft: 12, paddingRight: 8,
                  height: 36, width: 280,
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                }}
              >
                <FaSearch style={{ color: '#94a3b8', flexShrink: 0 }} size={11} />
                <input
                  type="text"
                  value={search}
                  onChange={handleSearch}
                  placeholder="Search in Drive..."
                  style={{
                    appearance: 'none', WebkitAppearance: 'none',
                    boxShadow: 'none', margin: 0, padding: 0,
                    border: 'none', outline: 'none',
                    backgroundColor: 'transparent',
                    flex: 1, minWidth: 0,
                    fontSize: 12, fontWeight: 500,
                    color: '#0f172a', fontFamily: 'inherit', lineHeight: 1,
                  }}
                />
                {search && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    style={{
                      appearance: 'none', WebkitAppearance: 'none',
                      width: 18, height: 18, padding: 0, margin: 0,
                      backgroundColor: 'transparent', border: 'none',
                      color: '#94a3b8', cursor: 'pointer',
                      fontSize: 12, lineHeight: 1,
                    }}
                    aria-label="Clear"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* View toggle */}
              <div
                style={{
                  display: 'flex', alignItems: 'center',
                  padding: 2, gap: 2,
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8, flexShrink: 0,
                }}
              >
                <TwIconButton icon={FaTh}   size="sm" variant="ghost" active={viewMode === 'grid'} onClick={() => setViewMode('grid')} title="Grid view" />
                <TwIconButton icon={FaList} size="sm" variant="ghost" active={viewMode === 'list'} onClick={() => setViewMode('list')} title="List view" />
              </div>

              <Button variant="secondary" size="sm" leftIcon={FaFolderPlus} onClick={() => setShowNewFolder(true)}>
                <span className="tw-hidden lg:tw-inline">New folder</span>
              </Button>
              <Button variant="primary" size="sm" leftIcon={FaCloudUploadAlt} onClick={() => setShowUpload(true)}>
                <span className="tw-hidden lg:tw-inline">Upload</span>
              </Button>
              <Button variant="secondary" size="sm" leftIcon={FaTrashAlt} onClick={() => navigate('/drives/trash/list')}>
                <span className="tw-hidden md:tw-inline">Trash</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Sub-toolbar: breadcrumb + filters */}
        <div className="tw-px-4 sm:tw-px-6 tw-pb-3 tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
          {/* Breadcrumb with back button */}
          <nav className="tw-flex tw-items-center tw-gap-1 tw-text-[12px] tw-font-bold tw-min-w-0 tw-flex-1" aria-label="Breadcrumb">
            {parentUrl && (
              <>
                <TwIconButton
                  icon={FaArrowLeft}
                  size="sm"
                  variant="soft"
                  onClick={() => navigate(parentUrl)}
                  title="Go up one folder"
                />
                <span style={{ width: 6 }} />
              </>
            )}
            <Link
              to={DRIVE_ROOT}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '0 8px', height: 24,
                borderRadius: 6,
                fontSize: 12, fontWeight: 700,
                color: '#64748b', textDecoration: 'none',
                transition: 'background-color 120ms',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#0f172a'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
            >
              <FaHome size={16} /> <span></span>
            </Link>
            {ancestorData?.map((a) => (
              <React.Fragment key={a.id}>
                <FaChevronRightSep style={{ color: '#cbd5e1' }} size={8} />
                <Link
                  to={`/drives/${a.id}`}
                  title={a.name}
                  style={{
                    display: 'inline-flex', alignItems: 'center',
                    padding: '0 8px', height: 24,
                    borderRadius: 6,
                    fontSize: 12, fontWeight: 700,
                    color: '#64748b', textDecoration: 'none',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160,
                    transition: 'background-color 120ms',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#0f172a'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
                >
                  {a.name}
                </Link>
              </React.Fragment>
            ))}
            {currentFolder && (
              <>
                <FaChevronRightSep style={{ color: '#cbd5e1' }} size={8} />
                <span style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '0 8px', height: 24,
                  fontSize: 12, fontWeight: 700,
                  color: '#0f172a',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240,
                }} title={currentFolder}>
                  {currentFolder}
                </span>
              </>
            )}
          </nav>

          {/* Right side: filters */}
          <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
            <button
              type="button"
              onClick={toggleSelectAll}
              style={{
                appearance: 'none', WebkitAppearance: 'none',
                boxShadow: 'none', margin: 0,
                display: 'inline-flex', alignItems: 'center', gap: 8,
                height: 32, padding: '0 12px 0 10px',
                backgroundColor: isAllSelected ? '#2563eb' : '#ffffff',
                color: isAllSelected ? '#ffffff' : '#475569',
                border: `1px solid ${isAllSelected ? '#2563eb' : '#e2e8f0'}`,
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 12, fontWeight: 700,
                fontFamily: 'inherit', lineHeight: 1,
                transition: 'background-color 120ms ease, border-color 120ms ease',
              }}
            >
              <span style={{
                width: 16, height: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: isAllSelected ? '#ffffff' : 'transparent',
                color: isAllSelected ? '#2563eb' : 'transparent',
                border: `1.5px solid ${isAllSelected ? '#ffffff' : '#94a3b8'}`,
                borderRadius: 4,
              }}>
                {isAllSelected && <FaCheck size={8} />}
              </span>
              Select all
            </button>

            <TwDropdown value={pageSize}  onChange={handlePageSizeChange} options={PAGE_SIZE_OPTIONS} size="sm" minWidth="120px" />
            <TwDropdown value={sortValue} onChange={handleSortChange}     options={SORT_OPTIONS}      size="sm" minWidth="140px" leftIcon={FaSortAmountDown} />
          </div>
        </div>

        {/* Bulk action bar */}
        {selectedIds.length > 0 && (
          <div className="tw-px-4 sm:tw-px-6 tw-py-2 tw-bg-blue-600 tw-text-white tw-flex tw-items-center tw-gap-3">
            <span className="tw-text-xs tw-font-bold">{selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected</span>
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              style={{
                appearance: 'none', WebkitAppearance: 'none', boxShadow: 'none',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                margin: 0, padding: '4px 8px',
                fontSize: 12, fontWeight: 700, lineHeight: 1.4,
                color: '#ffffff', backgroundColor: 'transparent',
                border: 'none', borderRadius: 6,
                cursor: 'pointer', fontFamily: 'inherit',
                transition: 'background-color 120ms ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <FaTimes size={10} />
              Clear
            </button>
            <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
              <Button variant="ghostOnDark" size="sm" leftIcon={FaDownload} onClick={handleBulkDownload}>
                Download
              </Button>
              <Button variant="danger" size="sm" leftIcon={FaTrash} onClick={() => setTrashTarget('bulk')}>
                Move to trash
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ── BODY (fills available height) ─────────────────── */}
      <div
        style={{
          flex: 1, minHeight: 0,
          display: 'flex', flexDirection: 'column',
          borderTop: '1px solid #e2e8f0',
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
        }}
      >
        {loading ? (
          <div style={{ padding: '20px 24px', overflowY: 'auto' }} className={viewMode === 'grid'
            ? 'tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-5'
            : 'tw-space-y-1'
          }>
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className={`tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg tw-animate-pulse ${viewMode === 'grid' ? 'tw-h-[210px]' : 'tw-h-14'}`}
              />
            ))}
          </div>
        ) : data.length === 0 ? (
          <div style={{
            flex: 1, minHeight: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            padding: '32px', textAlign: 'center',
          }}>
            <div className="tw-w-20 tw-h-20 tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-200 tw-flex tw-items-center tw-justify-center tw-text-slate-300 tw-mb-5">
              <FaFolderOpen size={32} />
            </div>
            <h3 className="tw-text-base tw-font-bold tw-text-slate-800 tw-m-0">This folder is empty</h3>
            <p className="tw-text-sm tw-text-slate-500 tw-m-0 tw-mt-1 tw-mb-5 tw-font-medium">Drop files here or click Upload to get started.</p>
            <Button variant="primary" size="md" leftIcon={FaCloudUploadAlt} onClick={() => setShowUpload(true)}>
              Upload files
            </Button>
          </div>
        ) : viewMode === 'grid' ? (
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px' }}>
            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-4 lg:tw-grid-cols-5 xl:tw-grid-cols-6 tw-gap-3">
              {data.map((item, idx) => (
                <DriveGridItem
                  key={item.id}
                  item={item}
                  index={idx}
                  isSelected={selectedIds.includes(item.id)}
                  onToggleSelect={toggleSelect}
                  onOpen={handleOpen}
                  handleAction={handleAction}
                />
              ))}
            </div>
          </div>
        ) : (
          <div style={{
            flex: 1, minHeight: 0,
            display: 'flex', flexDirection: 'column',
            padding: '20px 24px',
          }}>
            <div style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              flex: 1, minHeight: 0,
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}>
              <div
                className="tw-hidden sm:tw-flex"
                style={{
                  flexShrink: 0,
                  alignItems: 'center', gap: 12,
                  padding: '10px 14px',
                  backgroundColor: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  fontSize: 10, fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                }}
              >
                <div style={{ width: 18, flexShrink: 0 }} />
                <div style={{ width: 36, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>Name</div>
                <div className="tw-hidden md:tw-block" style={{ width: 144, flexShrink: 0 }}>Owner</div>
                <div className="tw-hidden lg:tw-block" style={{ width: 112, flexShrink: 0 }}>Modified</div>
                <div className="tw-hidden sm:tw-block" style={{ width: 80, flexShrink: 0, textAlign: 'right' }}>Size</div>
                <div style={{ width: 156, flexShrink: 0, textAlign: 'right' }}>Actions</div>
              </div>
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {data.map((item, idx) => (
                  <DriveListItem
                    key={item.id}
                    item={item}
                    index={idx}
                    isSelected={selectedIds.includes(item.id)}
                    onToggleSelect={toggleSelect}
                    onOpen={handleOpen}
                    handleAction={handleAction}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── FOOTER ──────────────────────────────────── */}
      {totalRows > 0 && (
        <div className="tw-flex-shrink-0 tw-border-t tw-border-slate-200 tw-bg-white tw-px-4 sm:tw-px-6 tw-py-2 tw-flex tw-items-center tw-justify-between tw-gap-3">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, fontSize: 12, color: '#64748b', fontWeight: 500 }}>
            <span className="tw-hidden sm:tw-inline">
              Showing&nbsp;
              <strong style={{ color: '#0f172a', fontWeight: 700, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>{fromRecord}–{toRecord}</strong>
              &nbsp;of&nbsp;
              <strong style={{ color: '#0f172a', fontWeight: 700, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>{totalRows}</strong>
            </span>
            <span className="sm:tw-hidden" style={{ fontWeight: 600 }}>
              <strong style={{ color: '#0f172a', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>{totalRows}</strong> items
            </span>

            <span aria-hidden="true" style={{ width: 1, height: 16, backgroundColor: '#cbd5e1' }} className="tw-hidden md:tw-block" />

            <span className="tw-hidden md:tw-inline-flex" style={{ alignItems: 'center', gap: 8 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px',
                backgroundColor: '#eff6ff', color: '#2563eb',
                border: '1px solid #dbeafe',
                borderRadius: 999, fontWeight: 700, fontSize: 11,
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
              }}>
                {folderCount}
                <span style={{ fontFamily: 'inherit', fontWeight: 600, opacity: 0.8 }}>folder</span>
              </span>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px',
                backgroundColor: '#f1f5f9', color: '#475569',
                border: '1px solid #e2e8f0',
                borderRadius: 999, fontWeight: 700, fontSize: 11,
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
              }}>
                {fileCount}
                <span style={{ fontFamily: 'inherit', fontWeight: 600, opacity: 0.8 }}>file</span>
              </span>
              {totalBytes > 0 && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px',
                  backgroundColor: '#fef2f2', color: '#dc2626',
                  border: '1px solid #fecaca',
                  borderRadius: 999, fontWeight: 700, fontSize: 11,
                  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                }}>
                  {getSize(totalBytes)}
                  <span style={{ fontFamily: 'inherit', fontWeight: 600, opacity: 0.8 }}>total</span>
                </span>
              )}
            </span>
          </div>
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <TwIconButton icon={FaChevronLeft}  size="sm" variant="secondary" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1}            title="Previous page" />
            <span className="tw-text-xs tw-font-mono tw-font-bold tw-text-slate-700 tw-px-2 tw-tabular-nums">{currentPage} / {totalPages || 1}</span>
            <TwIconButton icon={FaChevronRight} size="sm" variant="secondary" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages} title="Next page" />
          </div>
        </div>
      )}

      {/* ── MODALS ────────────────────────────────────── */}
      <DrivePreviewModal asset={previewAsset} onClose={() => setPreviewAsset(null)} handleAction={handleAction} />
      <DriveUploadModal show={showUpload} onClose={() => setShowUpload(false)} assetId={assetId} onSuccess={refetch} />
      <DriveFolderForm show={showNewFolder} onClose={() => setShowNewFolder(false)} />
      <DriveFolderForm show={Boolean(renameAsset)} onClose={() => setRenameAsset(null)} asset={renameAsset} />

      <DriveDeleteConfirm
        show={Boolean(trashTarget) && trashTarget !== 'bulk'}
        onClose={() => setTrashTarget(null)}
        onConfirm={async () => {
          try { await dispatch(moveToTrashAsset(trashTarget.id)).unwrap(); setTrashTarget(null); refetch(); }
          catch { /* slice toasts */ }
        }}
        title="Move to trash"
        message="Are you sure you want to move this item to trash?"
        itemName={trashTarget?.name}
        confirmLabel="Move to trash"
      />

      <DriveDeleteConfirm
        show={trashTarget === 'bulk'}
        onClose={() => setTrashTarget(null)}
        onConfirm={async () => { await performBulkTrash(); setTrashTarget(null); }}
        title="Move to trash"
        message={`Are you sure you want to move ${selectedAssets.length} item(s) to trash?`}
        itemNames={selectedAssets.map((a) => a.name)}
        confirmLabel="Move to trash"
      />

      <DriveShareForm show={!!shareAsset} onClose={() => setShareAsset(null)} asset={shareAsset} />
      <DriveGetLinkModal show={Boolean(getLinkAsset)} onClose={() => setGetLinkAsset(null)} asset={getLinkAsset} />
      <DriveDetailDrawer show={Boolean(detailAsset)} onClose={() => setDetailAsset(null)} asset={detailAsset} />
      {deleteForeverAsset && <DriveDeleteForeverConfirm showModal setshowModal={() => setDeleteForeverAsset(null)} asset={deleteForeverAsset} />}
    </div>
  );
};

export default DriveListPage;
